import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { AppShell } from "../components/Shell";
import { StepRace, StepLevel, StepGoal, StepStrengths, StepAvailability } from "../components/onboarding/Steps";
import { useAuth } from "../context/AuthContext";
import { api, errorMessage } from "../lib/api";
import { STATIONS } from "../constants/rox";
import { useT } from "../i18n";

const BG = "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=1200&q=70";

export default function Onboarding() {
  const { user, profile, setProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  useEffect(() => { if (error) window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); }, [error]);
  const [busy, setBusy] = useState(false);
  const [races, setRaces] = useState([]);
  const { t } = useT();
  useEffect(() => {
    api.get("/races").then((r) => setRaces(r.data)).catch(() => {});
  }, []);
  const [data, setData] = useState(() => ({
    first_name: profile?.first_name || location.state?.first_name || user?.first_name || "",
    last_name: profile?.last_name || user?.last_name || "",
    city: profile?.city || location.state?.city || user?.city || "",
    city_country: profile?.city_country || null,
    city_lat: profile?.city_lat ?? null,
    city_lon: profile?.city_lon ?? null,
    birth_date: profile?.birth_date || null,
    search_status: profile?.search_status || "searching",
    results: (profile?.results || []).map(({ city, year, format, time_seconds }) => ({ city, year, format, time_seconds })),
    race: profile?.race || "",
    category: profile?.category || "",
    gender: profile?.gender || "",
    level: profile?.level || "",
    goal: profile?.goal || "",
    target_time: profile?.target_time || 85,
    availability: profile?.availability || [],
    scores: profile?.scores || Object.fromEntries(STATIONS.map((s) => [s.key, 5])),
    instagram: profile?.instagram || "",
    bio: profile?.bio || "",
    photo_url: profile?.photo_url || null,
  }));
  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const steps = [StepRace, StepLevel, StepGoal, StepStrengths, StepAvailability];
  const Step = steps[step];
  const valid = [
    () => data.city && data.race && data.category && data.gender,
    () => data.level,
    () => data.goal,
    () => true,
    () => data.availability.length > 0,
  ][step]();

  const next = async () => {
    setError("");
    if (step < steps.length - 1) return setStep(step + 1);
    setBusy(true);
    try {
      const payload = { ...data, target_time: Number(data.target_time), first_name: data.first_name || user.first_name || "Athlète" };
      const { data: saved } = await api.put("/profile/me", payload);
      setProfile(saved);
      navigate("/recherche", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell narrow>
      <div className="relative flex min-h-screen flex-col" data-testid="onboarding-page">
        <img src={BG} alt="" className="pointer-events-none absolute inset-x-0 bottom-0 h-64 w-full object-cover opacity-20 grayscale [mask-image:linear-gradient(to_top,black,transparent)]" />
        <TopBar compact onBack={step === 0 ? () => navigate(profile ? "/profil" : "/") : () => setStep(step - 1)} />
        <div className="px-5">
          <div className="flex items-center gap-3">
            <div className="h-1 flex-1 overflow-hidden rounded-sm bg-rox-gray">
              <div className="h-full bg-rox-orange transition-[width] duration-500" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
            </div>
            <span className="text-xs font-semibold tracking-wider text-rox-muted" data-testid="onboarding-step-indicator">{t("ob_step", step + 1, steps.length)}</span>
          </div>
        </div>
        <main key={step} className="relative flex-1 px-5 pb-32 pt-6 animate-rise-in">
          <Step data={data} update={update} races={races} />
          {error && <p data-testid="onboarding-error" className="mt-4 text-sm text-rox-orange">{error}</p>}
        </main>
        <div className="fixed inset-x-0 bottom-0 z-20 mx-auto w-full max-w-md bg-gradient-to-t from-rox-black via-rox-black/95 to-transparent px-5 pb-6 pt-8 lg:max-w-[640px]">
          <button data-testid="onboarding-next" className="btn-primary" disabled={!valid || busy} onClick={next}>
            {busy ? "..." : step === steps.length - 1 ? t("ob_finish") : t("ob_continue")} <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </AppShell>
  );
}
